import { useRef, useEffect, memo } from "react";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { useIonViewDidEnter, useIonViewDidLeave } from "@ionic/react";

interface Props {
  animationSpeed?: number;
  animationData: any;
  className?: string;
  animationInterval?: number | null;
  autoplay?: boolean;
  justOnce?: boolean;
  onComplete?: () => void;
  onDOMLoaded?: () => void;
}

const WLLottie: React.FC<Props> = ({
  animationSpeed = 1,
  animationData,
  className,
  animationInterval = 3000,
  justOnce = false,
  autoplay = false,
  onComplete,
  onDOMLoaded,
}) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useIonViewDidEnter(() => {
    // resume animation when entering
    lottieRef.current?.play();
  });

  useIonViewDidLeave(() => {
    // stop animation when leaving page
    lottieRef.current?.stop();
  });

  useEffect(() => {
    if (!lottieRef.current || autoplay) return;

    const playAnimation = () => {
      if (lottieRef.current) {
        lottieRef.current.setSpeed(animationSpeed);
        lottieRef.current.stop();
        lottieRef.current.play();
      }
    };

    if (justOnce) {
      playAnimation();
      return;
    }
    if (animationInterval !== null) {
      const interval = setInterval(playAnimation, animationInterval);
      return () => {
        clearInterval(interval);
        lottieRef?.current?.stop();
      };
    } else {
      return () => {
        lottieRef?.current?.stop();
      };
    }
  }, [animationSpeed, animationInterval, justOnce, autoplay]);

  return (
    <Lottie
      lottieRef={lottieRef}
      className={className}
      animationData={animationData}
      loop={autoplay}
      autoplay={autoplay}
      onComplete={onComplete}
      onDOMLoaded={onDOMLoaded}
    />
  );
};

export default memo(WLLottie);
